/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ActionButton } from '@fluentui/react/lib/Button';
import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';

import { TemplateScope } from '../../../../../apollo/reactiveVariables/TemplateManagementVariables';
import orgWideScopeImage from '../../../../../assets/org-wide-scope.png';
import specificPeopleScopeImage from '../../../../../assets/specific-people-scope.png';
import teamWideScopeImage from '../../../../../assets/team-wide-scope.png';

import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { ErrorPage } from '../../../../containers/ErrorPage';
import { LoadingPage } from '../../../../containers/LoadingPage';
import { SelectableButton } from '../../../SelectableButton';
import { getBodyStyles, getBodyTokens, getPageStyles, getTitleStyles } from '../../PreSetupTemplateWizard.styles';

export interface ViewScopeComponentProps {
    onScopeSelected?: (scope: TemplateScope) => void;
    onBack?: () => void;
    showTeamScope?: boolean;
    isLoading?: boolean;
    showError?: boolean;
    setShowError?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ViewScopeComponent: React.FC<ViewScopeComponentProps> = (props: ViewScopeComponentProps): JSX.Element => {
    const loc = getAppHost().getLocale();
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;

    const { onScopeSelected, onBack, showTeamScope, isLoading, showError, setShowError } = props;

    return (
        <Stack styles={getPageStyles}>
            <Stack.Item>
                <Label role='alert' aria-label={loc.strings.select_scope_aria_label} styles={getTitleStyles(theme)}>
                    {loc.strings.select_scope_text}
                </Label>
            </Stack.Item>
            <Stack.Item grow>
                {isLoading ? (
                    <LoadingPage />
                ) : showError ? (
                    <ErrorPage
                        onAction={(): void => {
                            setShowError(false);
                        }}
                    />
                ) : (
                    <Stack styles={getBodyStyles} tokens={getBodyTokens}>
                        <SelectableButton
                            onSelected={onScopeSelected}
                            id={TemplateScope.TenantAll}
                            title={loc.strings.org_wide_option_text}
                            description={loc.strings.org_wide_option_description}
                            imageSrc={orgWideScopeImage}
                        />
                        <SelectableButton
                            onSelected={onScopeSelected}
                            id={TemplateScope.TenantSpecificPeople}
                            title={loc.strings.specific_people_option_text}
                            description={loc.strings.specific_people_option_description}
                            imageSrc={specificPeopleScopeImage}
                        />
                        {showTeamScope && (
                            <SelectableButton
                                onSelected={onScopeSelected}
                                id={TemplateScope.Team}
                                title={loc.strings.team_wide_option_text}
                                description={loc.strings.team_wide_option_description}
                                imageSrc={teamWideScopeImage}
                            />
                        )}
                    </Stack>
                )}
            </Stack.Item>
            <Stack.Item>
                <Stack horizontal verticalAlign='center' horizontalAlign='space-between'>
                    <ActionButton
                        iconProps={{ iconName: 'ChevronLeft' }}
                        text={loc.strings.carousel_back}
                        ariaLabel={loc.strings.carousel_back_aria_label}
                        onClick={onBack}
                    />
                </Stack>
            </Stack.Item>
        </Stack>
    );
};
